import type {
  IdentityMembershipSummary,
  RequestOtpResponse,
} from "@ghalishor/contracts";
import type { IdentitySessionSnapshot } from "./session-store";

export const LOGIN_INTENT_STORAGE_KEY = "ghalishor.loginIntent" as const;

export type LoginIntent = "customer" | "business" | "platform";

export const IDENTITY_WEB_ROUTES = Object.freeze({
  home: "/",
  login: "/login",
  loginCustomer: "/login/customer",
  loginBusiness: "/login/business",
  loginPlatform: "/login/platform",
  verify: "/login/verify",
  resetPassword: "/login/reset-password",
  customerHome: "/customer/home",
  customerProfile: "/customer/profile",
  customerRequests: "/customer/requests",
  customerRequestNew: "/customer/requests/new",
  customerRequestEdit: "/customer/requests/edit",
  customerRequestDetail: "/customer/requests/detail",
  customerMessages: "/messages",
  businessSelect: "/business/select",
  businessCreate: "/business/create",
  businessHome: "/business/home",
  businessInbox: "/business/inbox",
  businessActive: "/business/jobs",
  businessProfile: "/business/profile",
  businessMessages: "/business/messages",
  businessConsole: "/business/console",
  businessConsoleRates: "/business/console/rates",
  businessConsoleLogistics: "/business/console/logistics",
  businessConsoleReports: "/business/console/reports",
  businessConsoleTeam: "/business/console/team",
  businessConsoleFleet: "/business/console/fleet",
  businessConsoleProfile: "/business/console/profile",
  businessConsoleSettings: "/business/console/settings",
  partners: "/partners",
  downloads: "/downloads",
  platformHome: "/platform",
  platformPartnerships: "/platform/partnerships",
  platformBusinesses: "/platform/businesses",
  platformUsers: "/platform/users",
  platformRequests: "/platform/requests",
  platformRequestDetail: "/platform/requests/detail",
  platformAudit: "/platform/audit",
  platformNotifications: "/platform/notifications",
  platformMessages: "/platform/messages",
  account: "/account",
  passwordChange: "/account/password-change",
} as const);

export type IdentityWebRoute =
  (typeof IDENTITY_WEB_ROUTES)[keyof typeof IDENTITY_WEB_ROUTES];

const ROUTES = new Set<string>(Object.values(IDENTITY_WEB_ROUTES));

const PLATFORM_ROUTES = new Set<IdentityWebRoute>([
  IDENTITY_WEB_ROUTES.platformHome,
  IDENTITY_WEB_ROUTES.platformPartnerships,
  IDENTITY_WEB_ROUTES.platformBusinesses,
  IDENTITY_WEB_ROUTES.platformUsers,
  IDENTITY_WEB_ROUTES.platformRequests,
  IDENTITY_WEB_ROUTES.platformRequestDetail,
  IDENTITY_WEB_ROUTES.platformAudit,
  IDENTITY_WEB_ROUTES.platformNotifications,
  IDENTITY_WEB_ROUTES.platformMessages,
]);

export function isPlatformWebRoute(route: IdentityWebRoute): boolean {
  return PLATFORM_ROUTES.has(route);
}

export interface PendingOtpChallenge {
  readonly mobile: string;
  readonly challengeId: string;
  readonly expiresAt: string;
  readonly resendAfter: string;
  readonly devCode: string | null;
  readonly intent: LoginIntent;
}

export class IdentityWebFlow {
  private pendingOtp: PendingOtpChallenge | null = null;

  setPendingOtp(
    mobile: string,
    response: RequestOtpResponse,
    intent: LoginIntent,
  ): void {
    this.pendingOtp = Object.freeze({
      mobile,
      challengeId: response.challengeId,
      expiresAt: response.expiresAt,
      resendAfter: response.resendAfter,
      devCode: response._devCode ?? null,
      intent,
    });
  }

  getPendingOtp(): PendingOtpChallenge | null {
    return this.pendingOtp;
  }

  clearPendingOtp(): void {
    this.pendingOtp = null;
  }
}

export function resolveIdentityWebRoute(pathname: string): IdentityWebRoute {
  const trimmed = pathname.split("?")[0]?.split("#")[0] ?? "";
  const normalized =
    trimmed === "" || trimmed === "/"
      ? "/"
      : `/${trimmed.replace(/^\/+|\/+$/g, "")}`;
  if (ROUTES.has(normalized)) return normalized as IdentityWebRoute;
  return IDENTITY_WEB_ROUTES.home;
}

export function firstActiveMembership(
  memberships: readonly IdentityMembershipSummary[],
): IdentityMembershipSummary | null {
  return (
    memberships.find((membership) => membership.status === "active") ?? null
  );
}

export function routeAfterAuthentication(
  memberships: readonly IdentityMembershipSummary[],
  selectedBusinessId: string | null,
  intent: LoginIntent | null = null,
): IdentityWebRoute {
  const effectiveIntent = intent ?? "customer";

  if (effectiveIntent === "platform") {
    return IDENTITY_WEB_ROUTES.platformHome;
  }

  if (effectiveIntent === "customer") {
    return IDENTITY_WEB_ROUTES.customerHome;
  }

  if (memberships.length === 0) {
    return IDENTITY_WEB_ROUTES.businessCreate;
  }

  if (
    selectedBusinessId !== null &&
    memberships.some(
      (membership) =>
        membership.businessId === selectedBusinessId &&
        membership.status === "active" &&
        membership.businessStatus === "active",
    )
  ) {
    return IDENTITY_WEB_ROUTES.businessHome;
  }

  return IDENTITY_WEB_ROUTES.businessSelect;
}

export function enforceIdentityRoute(
  requestedRoute: IdentityWebRoute,
  session: IdentitySessionSnapshot,
  hasPendingOtp: boolean,
  intent: LoginIntent | null = null,
  surface: "public" | "platform" = "public",
  mustChangePassword = false,
): IdentityWebRoute {
  if (surface === "platform" && session.impersonation === null) {
    if (!session.authenticated) {
      if (
        requestedRoute === IDENTITY_WEB_ROUTES.loginPlatform ||
        requestedRoute === IDENTITY_WEB_ROUTES.verify
      ) {
        if (requestedRoute === IDENTITY_WEB_ROUTES.verify && !hasPendingOtp) {
          return IDENTITY_WEB_ROUTES.loginPlatform;
        }
        return requestedRoute;
      }
      return IDENTITY_WEB_ROUTES.loginPlatform;
    }
    if (mustChangePassword) {
      return IDENTITY_WEB_ROUTES.passwordChange;
    }
    if (
      requestedRoute === IDENTITY_WEB_ROUTES.loginPlatform ||
      requestedRoute === IDENTITY_WEB_ROUTES.verify ||
      requestedRoute === IDENTITY_WEB_ROUTES.home ||
      requestedRoute === IDENTITY_WEB_ROUTES.login
    ) {
      return IDENTITY_WEB_ROUTES.platformHome;
    }
    if (
      isPlatformWebRoute(requestedRoute) ||
      requestedRoute === IDENTITY_WEB_ROUTES.account ||
      requestedRoute === IDENTITY_WEB_ROUTES.passwordChange
    ) {
      return requestedRoute;
    }
    return IDENTITY_WEB_ROUTES.platformHome;
  }

  // Public surface: no platform card on home, but allow a temporary deep-link
  // to /login/platform (and /platform/* after auth) until admin.ghalishorapp.ir is live.
  if (requestedRoute === IDENTITY_WEB_ROUTES.loginPlatform) {
    if (!session.authenticated) return requestedRoute;
    if (mustChangePassword) return IDENTITY_WEB_ROUTES.passwordChange;
    return IDENTITY_WEB_ROUTES.platformHome;
  }
  if (isPlatformWebRoute(requestedRoute)) {
    if (!session.authenticated) return IDENTITY_WEB_ROUTES.loginPlatform;
    if (mustChangePassword) return IDENTITY_WEB_ROUTES.passwordChange;
    return requestedRoute;
  }

  const isGate =
    requestedRoute === IDENTITY_WEB_ROUTES.home ||
    requestedRoute === IDENTITY_WEB_ROUTES.login ||
    requestedRoute === IDENTITY_WEB_ROUTES.loginCustomer ||
    requestedRoute === IDENTITY_WEB_ROUTES.loginBusiness;

  const isVerify = requestedRoute === IDENTITY_WEB_ROUTES.verify;
  const isPublicPartners = requestedRoute === IDENTITY_WEB_ROUTES.partners;
  const isPublicDownloads = requestedRoute === IDENTITY_WEB_ROUTES.downloads;
  const isPasswordReset = requestedRoute === IDENTITY_WEB_ROUTES.resetPassword;

  if (!session.authenticated) {
    if (isPublicPartners || isPublicDownloads || isPasswordReset)
      return requestedRoute;
    if (isVerify && hasPendingOtp) return requestedRoute;
    if (isGate || isVerify) {
      if (requestedRoute === IDENTITY_WEB_ROUTES.login)
        return IDENTITY_WEB_ROUTES.home;
      return isGate ? requestedRoute : IDENTITY_WEB_ROUTES.home;
    }
    return IDENTITY_WEB_ROUTES.home;
  }

  if (isPasswordReset) return requestedRoute;

  if (mustChangePassword) {
    return IDENTITY_WEB_ROUTES.passwordChange;
  }

  if (isGate || isVerify) {
    return routeAfterAuthentication(
      session.memberships,
      session.selectedBusinessId,
      intent,
    );
  }

  if (
    (requestedRoute === IDENTITY_WEB_ROUTES.businessHome ||
      requestedRoute === IDENTITY_WEB_ROUTES.businessInbox ||
      requestedRoute === IDENTITY_WEB_ROUTES.businessActive ||
      requestedRoute === IDENTITY_WEB_ROUTES.businessProfile ||
      requestedRoute === IDENTITY_WEB_ROUTES.businessMessages ||
      requestedRoute === IDENTITY_WEB_ROUTES.businessConsole ||
      requestedRoute === IDENTITY_WEB_ROUTES.businessConsoleRates ||
      requestedRoute === IDENTITY_WEB_ROUTES.businessConsoleLogistics ||
      requestedRoute === IDENTITY_WEB_ROUTES.businessConsoleReports ||
      requestedRoute === IDENTITY_WEB_ROUTES.businessConsoleTeam ||
      requestedRoute === IDENTITY_WEB_ROUTES.businessConsoleFleet ||
      requestedRoute === IDENTITY_WEB_ROUTES.businessConsoleProfile ||
      requestedRoute === IDENTITY_WEB_ROUTES.businessConsoleSettings) &&
    (session.selectedBusinessId === null ||
      !session.memberships.some(
        (membership) =>
          membership.businessId === session.selectedBusinessId &&
          membership.status === "active" &&
          membership.businessStatus === "active",
      ))
  ) {
    return IDENTITY_WEB_ROUTES.businessSelect;
  }

  return requestedRoute;
}

export function readLoginIntent(
  storage: { getItem(key: string): string | null } | null,
): LoginIntent | null {
  const value = storage?.getItem(LOGIN_INTENT_STORAGE_KEY);
  return value === "customer" || value === "business" || value === "platform"
    ? value
    : null;
}

export function writeLoginIntent(
  storage: { setItem(key: string, value: string): void } | null,
  intent: LoginIntent,
): void {
  storage?.setItem(LOGIN_INTENT_STORAGE_KEY, intent);
}

export function clearLoginIntent(
  storage: { removeItem(key: string): void } | null,
): void {
  storage?.removeItem(LOGIN_INTENT_STORAGE_KEY);
}
